import { Component, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import * as Highcharts from 'highcharts';
import {
  PriceIndex,
  MarketService,
} from 'src/app/market/market.service';
import { ActivatedRoute } from '@angular/router';
import { DateRange } from 'src/app/shared/components/date-range.component';
import { DateTimeHelpers } from 'src/app/shared/helpers/date-time.helpers';
import _ from 'lodash';
import {
  PgDateRangeTitleComponent,
  PgDateRangeTitleEnum,
  PgTitleComponent,
  PgToolbarComponent,
} from '../../shared/components/title-bar/title-bar.component';
import { MarketToolbarComponent } from '../market-toolbar/market-toolbar.component';
import moment from 'moment';
import { Router } from '@angular/router';
import { SharedModule } from 'src/app/shared/shared.module';

interface IndexDiff {
  propaneDiff?: number;
  propanePercentage?: number;
  butaneDiff?: number;
  butanePercentage?: number;
  fiftyFiftyMix?: number;
  thirtySeventyMix?: number;
}

@Component({
  selector: 'app-market-report',
  templateUrl: './market-report.component.html',
  styleUrls: ['./market-report.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports:[SharedModule],
})
export class MarketReportComponent
implements
  OnInit,
  PgTitleComponent,
  PgDateRangeTitleComponent,
  PgToolbarComponent {
  title: EventEmitter<string> = new EventEmitter();
  dateRangeSubTitle = new EventEmitter<[DateRange, PgDateRangeTitleEnum]>();

  toolbarComponentType = MarketToolbarComponent;
  toolbarComponent!: MarketToolbarComponent;

  dateRange: DateRange;

  indexChanges: any[] = [];
  indexForCompare: any[] = [];
  firstIndex: any;
  secondIndex: any;
  diff: IndexDiff = {};

  dateArray: { date: string; timestamp: number; }[] = [];

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _marketService: MarketService,
    private _router: Router
  ) {
    this.dateRange = {
      startDate: moment().subtract(12, 'months').toDate(),
      endDate: moment().toDate()
    };
  }

  async ngOnInit() {
    this.getParameters();
    this.toolbarComponent.action = true;
    this.toolbarComponent.date.subscribe(dateRange => {
      this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this._router
          .navigate(['pricing/report/'], {
            queryParams: {
              startTime: dateRange?.startDate.getTime(),
              endTime: dateRange?.endDate.getTime()
            }
          })
          .then((e) => console.log(`Result of Navigation is ${e}`));
      });
    });
    this.dateArray = this.setDateArray(
      this.dateRange.startDate.getTime(),
      this.dateRange.endDate.getTime()
    );
    this.title.emit('Market Report');
    this.dateRangeSubTitle.emit([
      this.dateRange,
      PgDateRangeTitleEnum.defaultTitle,
    ]);
    const prices = (
      await this._marketService.getAll(
        this.dateRange.startDate.getTime(),
        this.dateRange.endDate.getTime()
      )
    )?.sort((a, b) => a.timestamp - b.timestamp) || [];

    const groupedByProduct = this.groupByProduct(prices);
    const lpgMax = Math.max(
      this.findMax(prices, 'Butane'),
      this.findMax(prices, 'Propane')
    );
    const blpg1Max = this.findMax(prices, 'BLPG1');
    const oilMax = this.findMax(prices, 'Crude Oil');
    const wetMax = this.findMax(prices, 'Wet');
    const bunkerMax = Math.max(
      this.findMax(prices, 'HFO'),
      this.findMax(prices, 'MGO'),
      this.findMax(prices, 'VLSFO')
    );

    const sofrMax = Math.max(
      this.findMax(prices, 'SOFR'),
      this.findMax(prices, 'average180day'),
      this.findMax(prices, 'average30day'),
      this.findMax(prices, 'average90day')
    );
    this.plotPerProduct(
      groupedByProduct.find((p) => p.productId === 'Butane'),
      'butaneReportDiv',
      0,
      lpgMax
    );
    this.plotPerProduct(
      groupedByProduct.find((p) => p.productId === 'Propane'),
      'propaneReportDiv',
      0,
      lpgMax
    );
    const d = this.federalReserve(groupedByProduct.filter((p) => (p.productId === 'SOFR' || p.productId === 'average90day' || p.productId === 'average30day' || p.productId === 'average180day')));
    this.plotPerProduct(
      d,
      'federalReserveDiv',
      0,
      sofrMax
    );
    if (groupedByProduct.find((p) => p.productId === 'BLPG1')) {
      this.plotPerProduct(
        groupedByProduct.find((p) => p.productId === 'BLPG1'),
        'balticReportDiv',
        0,
        blpg1Max
      );
    }

    if (groupedByProduct.find((p) => p.productId === 'Crude Oil')) {
      this.plotPerProduct(
        groupedByProduct.find((p) => p.productId === 'Crude Oil'),
        'crudOilReportDiv',
        0,
        oilMax
      );
    }

    if (groupedByProduct.find((p) => p.productId === 'Wet')) {
      this.plotPerProduct(
        groupedByProduct.find((p) => p.productId === 'Wet'),
        'demolitionReportDiv',
        0,
        wetMax
      );
    }

    const bunkerTypes = ['HFO', 'MGO', 'VLSFO'];
    const bunkerIndexSeries = groupedByProduct
      .filter((p) => bunkerTypes.some((t) => t === p.productId))
      .map((p) => ({
        productId: p.productId,
        series: p.series.filter((s) => s.name === 'Fujairah'),
      }));
    this.plotBunkerPrices(bunkerIndexSeries, 'bunkerReportDiv', 0, bunkerMax);

    this.indexChanges = this.findLatestByIndex(prices.filter(i => i.productId !== 'LNG' && i.productId !== 'Natural Gas'));
    this.toolbarComponent.dateRange = this.dateRange;
    this.indexForCompare = this.indexChanges
      .flatMap((i) => i.data)
      .filter((i) =>
        i.information.map((inf: any) => inf.product).includes('Propane') ||
        i.information.map((inf: any) => inf.product).includes('Butane')
      );
    const filstIndex = this.indexForCompare.find((i) => i.index === 'PG');
    const secondIndex = this.indexForCompare.find((i) => i.index === 'CP');
    this.firstIndex =
      filstIndex !== undefined ? filstIndex : this.indexForCompare[0];
    this.secondIndex =
      secondIndex !== undefined ? secondIndex : this.indexForCompare[1];
    this.compareTwoIndex();

    const aramco = prices.filter((p) => p.provider === 'Aramco');
    const baltic = prices.filter((p) => p.provider === 'Baltic');
    const blpg1 = baltic.filter((b) => b.productId === 'BLPG1');

    const aramcoByProduct = this.yoyGroupByProduct(aramco);
    const balticByProduct = this.yoyGroupByProduct(blpg1);
    const balticProduct = this.group(
      balticByProduct.filter((p) => p.productId === 'BLPG1'),
      'line'
    );
    const plotPerProduct = this.group(
      aramcoByProduct.filter((p) => p.productId === 'Propane'),
      'line'
    );
    const yoyProduct = this.group(
      aramcoByProduct.filter((p) => p.productId === 'Butane'),
      'line'
    );
    const barProduct = this.group(
      balticByProduct.filter((p) => p.productId === 'BLPG1'),
      'column'
    );
    this.yoyPlotPerProduct(
      yoyProduct,
      'yoyCPButaneReportDiv',
      'CP Butane',
      0,
      1000
    );
    this.yoyPlotPerProduct(
      balticProduct,
      'yoyBalticReportDiv',
      'BLPG1',
      0,
      100
    );
    this.yoyBarPlotPerProduct(
      barProduct,
      'yoyColumnBalticReportDiv',
      'BLPG1',
      0,
      100
    );
    this.yoyPlotPerProduct(
      plotPerProduct,
      'yoyCPPropaneReportDiv',
      'CP Propane',
      0,
      1000
    );
  }

  importantIndexes(index: string) {
    return (
      index === 'CP' ||
      index === 'Argus Middle East Index' ||
      index === 'PG' ||
      index === 'BLPG' ||
      index === 'Bunker Price' ||
      index === 'Europe Brent Spot Price FOB, Daily' ||
      index === 'Demolition-Bangladesh' ||
      index === 'Brent' ||
      index === 'WTI' ||
      index === 'Fujairah' ||
      index === 'VLGC'
    );
  }

  findMax(prices: PriceIndex[] | undefined, productId: string) {
    if(prices) {
      return Math.max(
        ...prices.filter((p) => p.productId === productId).map((p) => p.price)
      );
    } else {
      return 0;
    };
  }

  setDateArray(startTimestamp: number, endTimestamp: number): { date: string; timestamp: number; }[] {
    const startDate = moment(startTimestamp);
    const endDate = moment(endTimestamp);
    return Array.from(new Array(endDate.diff(startDate, 'days')), () => ({
      date: startDate.add(1, 'days').format('DD-MM-YYYY'),
      timestamp: startDate.unix() * 1000,
    }));
  }

  compareTwoIndex() {
    const butane1 = this.firstIndex.information.find(
      (i: any) => i.product === 'Butane'
    ).price.price;
    const proprane1 = this.firstIndex.information.find(
      (i: any) => i.product === 'Propane'
    ).price.price;
    const butane2 = this.secondIndex.information.find(
      (i: any) => i.product === 'Butane'
    ).price.price;
    const propane2 = this.secondIndex.information.find(
      (i: any) => i.product === 'Propane'
    ).price.price;
    this.diff = {
      propaneDiff: proprane1 - propane2,
      propanePercentage: (proprane1 - propane2) / propane2,
      butaneDiff: butane1 - butane2,
      butanePercentage: (butane1 - butane2) / butane2,
      fiftyFiftyMix: Math.round(
        0.5 * (proprane1 - propane2 + butane1 - butane2)
      ),
      thirtySeventyMix: Math.round(
        0.3 * (butane1 - butane2) + 0.7 * (proprane1 - propane2)
      ),
    };
  }

  getParameters() {
    this._activatedRoute.queryParams.subscribe(p => {
      const startDate = p['startTime'] ? +p['startTime'] : moment().subtract(1, 'year').toDate().getTime();
      const endDate = p['endTime'] ? +p['endTime'] : new Date();
      this.dateRange = {
        startDate: new Date(startDate),
        endDate: new Date(endDate)
      };
      this.toolbarComponent.dateRange = this.dateRange;
      this.title.emit('Market Report');
      this.dateRangeSubTitle.emit([this.dateRange, PgDateRangeTitleEnum.defaultTitle]);
    });
  }

  groupByProduct(data: PriceIndex[]) {
    return _.chain(data)
      .groupBy((i) => i.productId)
      .map((v, k) => ({
        productId: k,
        series: _.chain(v)
          .groupBy((i) => i.index)
          .map((v1, k1) => {
            const v2 = this.setMissingDataToNull(v1, this.dateArray);
            const i: [number, number][] = v2.map((j) => [j.timestamp, j.price]);
            const visiblity = this.importantIndexes(k1);
            return {
              name: k1,
              data: i,
              visible: visiblity,
              type: 'line',
            };
          })
          .value(),
      }))
      .value();
  }

  findLatestByIndex(data: PriceIndex[]) {
    const result = _.chain(data)
      .groupBy((i) => `${i.provider}§${i.index}`)
      .map((v, k) => {
        const productsPrices = _.chain(v)
          .groupBy((i) => i.productId)
          .map((v1, k1) => {
            const s = v1.sort((a, b) => a.timestamp - b.timestamp);
            const startOfMonth = moment(v1[v1.length - 1].timestamp)
              .startOf('month')
              .toDate();
            const s1 = v1
              .filter((t) => t.timestamp > startOfMonth.getTime())
              .sort((a, b) => a.timestamp - b.timestamp);
            const diffOffirst =
              s1.length > 0
                ? Math.round((s1[s1.length - 1].price - s1[0].price) * 100) /
                100
                : null;
            const different =
              v1.length > 1
                ? Math.round(
                  (s[v1.length - 1].price - s[v1.length - 2].price) * 100
                ) / 100
                : null;
            return {
              product: k1,
              diff: different,
              diffFromFirst: diffOffirst,
              price: s[v1.length - 1],
            };
          })
          .value();
        const keys: string[] = k.split('§');
        return {
          provider: keys[0],
          index: keys[1],
          information: productsPrices,
        };
      })
      .value();
    return _.chain(result)
      .groupBy((d) => d.provider)
      .map((v, k) => {
        const grouped = v.map((i) => {
          const ind = this.importantIndexes(i.index) ? 'show' : '';
          const propanePrice = i.information.find(
            (inf) => inf.product === 'Propane'
          );

          const butanePrice = i.information.find(
            (inf) => inf.product === 'Butane'
          );
          const fiftyFiftyMix =
            propanePrice !== undefined && butanePrice !== undefined
              ? 0.5 * (propanePrice.price.price + butanePrice.price.price)
              : null;
          const thirtySeventyMix =
            propanePrice !== undefined && butanePrice !== undefined
              ? 0.7 * propanePrice.price.price + 0.3 * butanePrice.price.price
              : null;
          const updatedColor =
            i.information.find(
              (s) =>
                s.price.timestamp >
                moment().add(-1, 'd').startOf('day').toDate().getTime()
            ) !== undefined
              ? 'green'
              : i.information.find(
                (s) =>
                  s.price.timestamp >
                  moment().add(-1, 'd').startOf('day').toDate().getTime()
              ) === undefined &&
                i.information.find(
                  (s) =>
                    s.price.timestamp >
                    moment().startOf('month').toDate().getTime()
                ) !== undefined
                ? '#ffa500'
                : 'red';
          if (thirtySeventyMix !== null) {
            return {
              index: i.index,
              expand: ind,
              information: i.information,
              fiftyFiftyMix: Math.round(fiftyFiftyMix || 0),
              thirtySeventyMix: Math.round(thirtySeventyMix),
              upToDateColor: updatedColor,
            };
          } else {
            return {
              index: i.index,
              expand: ind,
              information: i.information,
              upToDateColor: updatedColor,
            };
          }
        });
        return {
          id: k.replace(/\s+/g, ''),
          provider: k,
          data: grouped.sort((a, b) => ('' + b.expand).localeCompare(a.expand)),
        };
      })
      .value();
  }

  choosedProvider(provider: string) {
    return provider === 'go-shipping';
  }

  setMissingDataToNull(groupByProduct: PriceIndex[], dateArray: any[]) {
    const groubByProductHash = groupByProduct.reduce((hashMap: Record<string, any>, groupped) => {
      hashMap[moment(groupped.timestamp).format('DD-MM-YYYY')] = groupped;
      return hashMap;
    }, {});
    return dateArray
      .map((date) => {
        if (
          groubByProductHash[date.date] === undefined &&
          !DateTimeHelpers.isWeekend(date.timestamp) &&
          !this.choosedProvider(groupByProduct[0].provider)
        ) {
          return {
            provider: groupByProduct[0].provider,
            timestamp: date.timestamp,
            index: groupByProduct[0].index,
            productId: groupByProduct[0].productId,
            price: null,
            isDeleted: false,
          };
        } else {
          return groubByProductHash[date.date];
        }
      })
      .filter((d) => d !== undefined);
  }

  plotPerProduct(data: any, divId: string, yMin: number | null = null, yMax: number | null = null) {
    if (!data && data.series && data.series.length === 0) {
      return;
    }
    Highcharts.chart(divId, {
      chart: {
        type: 'line',
      },
      title: {
        text: `Daily ${data.productId}`,
      },
      yAxis: {
        title: {
          text: 'index',
        },
        labels: {
          formatter() {
            return '$ ' + this.value;
          },
        },
        min: yMin,
        max: yMax,
      },
      tooltip: {
        pointFormat:
          '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}',
        split: true,
        xDateFormat: '%Y-%m-%d',
      },
      xAxis: {
        type: 'datetime',
      },
      plotOptions: {
        line: {
          marker: {
            enabled: false,
          },
        },
      },
      series: data.series,
    });
  }

  plotBunkerPrices(data: any, divId: string, yMin: number| null = null, yMax: number | null = null) {
    if (!data && data.length === 0) {
      return;
    }

    const chartSeries = data.map((i: any) => ({
      name: i.productId,
      data: i.series[0].data,
      type: 'line',
    }));

    Highcharts.chart(divId, {
      chart: {
        type: 'line',
      },
      title: {
        text: 'Fujairah Bunker Index',
      },
      yAxis: {
        title: {
          text: 'index',
        },
        labels: {
          formatter() {
            return '$ ' + this.value;
          },
        },
        min: yMin,
        max: yMax,
      },
      tooltip: {
        pointFormat:
          '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}',
        split: true,
        xDateFormat: '%Y-%m-%d',
      },
      xAxis: {
        type: 'datetime',
      },
      plotOptions: {
        line: {
          marker: {
            enabled: false,
          },
        },
      },
      series: chartSeries,
    });
  }

  group(data: PriceIndex[], typeChart: string) {
    return _.chain(data)
      .groupBy((i) => new Date(i.timestamp).getFullYear())
      .map((i, key) => ({
        name: `${key}`,
        data: i.map((j) => [moment(j.timestamp).format('MMM'), j.price]),
        type: typeChart,
      }))
      .value();
  }

  yoyPlotPerProduct(
    chartSeries: any,
    divId: string,
    title: string,
    yMin: number,
    yMax: number
  ) {
    Highcharts.chart(divId, {
      chart: {
        type: 'line',
      },
      title: {
        text: `Year on Year ${title}`,
      },
      yAxis: {
        title: {
          text: 'index',
        },
        labels: {
          formatter() {
            return '$ ' + this.value;
          },
        },
        min: yMin,
        max: yMax,
      },
      tooltip: {
        pointFormat:
          '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}',
        split: true,
        xDateFormat: '%Y-%m-%d',
      },
      xAxis: {
        type: 'category',
      },
      plotOptions: {
        line: {
          marker: {
            enabled: false,
          },
        },
      },
      series: chartSeries,
    });
  }

  yoyBarPlotPerProduct(
    chartSeries: any,
    divId: string,
    title: string,
    yMin: number,
    yMax: number
  ) {
    Highcharts.chart(divId, {
      chart: {
        type: 'column',
      },
      title: {
        text: `Year on Year ${title}`,
      },
      yAxis: {
        title: {
          text: 'index',
        },
        labels: {
          formatter() {
            return '$ ' + this.value;
          },
        },
        min: yMin,
        max: yMax,
      },
      tooltip: {
        pointFormat:
          '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}',
        split: true,
        xDateFormat: '%Y-%m-%d',
      },
      xAxis: {
        type: 'category',
      },
      plotOptions: {
        line: {
          marker: {
            enabled: false,
          },
        },
      },
      series: chartSeries,
    });
  }

  private yoyGroupByProduct(data: PriceIndex[]): PriceIndex[] {
    return _.chain(data)
      .map((d) => ({
        provider: d.provider,
        index: d.index,
        timestamp: moment(d.timestamp).startOf('month').unix() * 1000,
        productId: d.productId,
        price: d.price,
        isDeleted: d.isDeleted,
      }))
      .groupBy((d) => `${d.productId}§${d.timestamp}`)
      .map((items) => {
        const d = items[0];
        return {
          provider: d.provider,
          index: d.index,
          timestamp: d.timestamp,
          productId: d.productId,
          price: _.round(
            _.meanBy(items, (i) => i.price),
            2
          ),
          isDeleted: d.isDeleted,
        };
      })
      .value();
  }

  uptodateColor(data: any) {
    return data.find(
      (s: any) =>
        s.price.timestamp >
        moment().add(-1, 'd').startOf('day').toDate().getTime()
    ) !== undefined
      ? 'green'
      : data.find(
        (s: any) =>
          s.price.timestamp >
          moment().add(-1, 'd').startOf('day').toDate().getTime()
      ) === undefined &&
        data.find(
          (s: any) =>
            s.price.timestamp > moment().startOf('month').toDate().getTime()
        ) !== undefined
        ? '#ffa500'
        : 'red';
  }

  federalReserve(data: any[]) {
    const d = data.map(d => ({
      ...d.series[0],
      name: d.productId,
      visible: true
    }));

    return {
      producId: 'SOFR',
      series: d
    };
  }
}
